import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import { rgba } from 'polished';

import Block from './Block';
import Button from './Button';

const Tab = styled(Button)`
  display: block;
  position: relative;
  border-radius: 1rem;
  margin-bottom: ${({ theme }) => theme.spacing.baseDown3};
  ${({ isActive }) => (isActive ? `pointer-events: none` : null)};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.white : rgba(theme.colors.white, 0.4)};
  padding: ${({ theme }) => `${theme.spacing.baseDown1}`};

  &:active,
  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
  }

  span {
    padding-bottom: ${({ theme }) => theme.spacing.baseDown3};
    padding-left: ${({ theme }) => theme.spacing.baseUnit};
    padding-right: ${({ theme }) => theme.spacing.baseUnit};
    ${({ theme, isActive }) =>
      isActive ? `box-shadow: 0 1px 0 ${rgba(theme.colors.white, 1)}` : null};
    color: ${({ theme }) => theme.colors.baseFont};
  }

  ${breakpoint('tablet')`
    margin-left: ${({ theme }) => theme.spacing.baseDown2};
    margin-bottom: 0;
    border-radius: 1rem 1rem 0 0;
    padding: ${({ theme }) =>
      `${theme.spacing.baseUnit} ${theme.spacing.baseDown2}`};
    z-index: ${({ isActive }) => (isActive ? '2' : '1')};

    &:first-of-type {
      margin-left: ${({ theme }) => theme.spacing.baseUp4};
    }
  `};
`;

const TabContent = styled(Block)`
  position: relative;
  border-radius: 1rem;

  ${breakpoint('tablet')`
    z-index: 1;
  `};

  ${breakpoint('tablet')`
    box-shadow: 0 -3px 5px rgba(0, 0, 0, 0.1);
  `};
`;

const Tabs = ({ children }) => {
  const tabs = children.filter((v) => v);
  const [activeTab, setActiveTab] = useState(tabs[0]?.props?.tabKey);

  return (
    <>
      <Block display="flex" flexDirection={{ mobile: 'column', tablet: 'row' }}>
        {tabs.map(({ props: { tabKey } }) => {
          const isActive = tabKey === activeTab;

          return (
            <Tab
              key={tabKey}
              onClick={() => setActiveTab(tabKey)}
              hoverColor={isActive ? 'white' : 'grayDark'}
              fontSize="baseDown1"
              isActive={isActive}
            >
              <span>{tabKey}</span>
            </Tab>
          );
        })}
      </Block>

      <TabContent
        p="baseUnit"
        backgroundColor="white"
        isFirstTabActive={activeTab === tabs[0]?.props?.tabKey}
      >
        {tabs.map(({ props: { tabKey, children: tabChildren } }) => {
          if (tabKey !== activeTab) {
            return null;
          }

          return tabChildren;
        })}
      </TabContent>
    </>
  );
};

Tabs.propTypes = {
  children: PropTypes.arrayOf(
    PropTypes.shape({
      props: PropTypes.shape({
        tabKey: PropTypes.string.isRequired,
        children: PropTypes.node.isRequired,
      }).isRequired,
    }),
  ).isRequired,
};

export default Tabs;
